import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  TableCell,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReactComponent as ReportsExcelIcon } from '~/assets/icons/reports-excel.svg';
import { ReactComponent as SubjectArrowDownIcon } from '~/assets/icons/arrow-down.svg';
import BaseTableReport from './components/base-table-report/BaseTableReport';
import { BaseDatePicker } from '~/components/common';
import reportApi, { parseBc01ResponsePayload, type IBc02Params } from '~/apis/report';
import { useAppSelector } from '~/redux/hooks';
import { handleApiError, notify } from '~/utils/helper';
import type { SortDirection } from '~/utils/types/common';
import { SortDirectionEnum } from '~/utils/enum';
import {
  bc02ViolationTypeParam,
  mapJourneyStatusColumnInRows,
  transformBc01HeadCells,
  transformBc01Rows,
  type Bc01TableRow,
  type ViolationFilterTypeKey,
} from './components/constants';
import { buildReportExcelFileName, downloadBc01ReportExcel } from './components/excelExport';
import { ReportTableMultilineCell } from './components/ReportTableMultilineCell';
import ReportSubjectFilter from './components/ReportSubjectFilter';
import { trackReportFetchEnd, trackReportFetchStart } from './components/reportFetchLoading';
import styles from './Report.module.scss';

dayjs.extend(customParseFormat);

const cx = classNames.bind(styles);

const REPORT_TABLE_CELL_CLASS = 'text-base font-normal text-[#161A1F] text-left align-top whitespace-normal';

function getDefaultViolationDateRange(): { start: Date; end: Date } {
  return {
    start: dayjs().startOf('day').toDate(),
    end: dayjs().endOf('day').toDate(),
  };
}

function formatBc01FromTo(startMs: number, endMs: number): { from: string; to: string } {
  const a = dayjs(startMs);
  const b = dayjs(endMs);
  const min = a.isBefore(b, 'day') ? a : b;
  const max = a.isAfter(b, 'day') ? a : b;
  return {
    from: min.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    to: max.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  };
}

const BC02_FETCH_DEBOUNCE_MS = 500;

const VIOLATION_MENU_KEYS: ViolationFilterTypeKey[] = ['all', 'deviceRemoval', 'fenceViolation', 'gathering'];

type Bc02ApiSnapshot = {
  headers: string[];
  data: string[][];
};

const ReportViolations = () => {
  const { t, i18n } = useTranslation('admin/report');
  const { t: tCommon } = useTranslation('admin/common');
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const username = selectedUser?.username ?? userProfile?.username;

  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [listReady, setListReady] = useState(false);
  const [personsCount, setPersonsCount] = useState(0);

  const [violationTypeAnchor, setViolationTypeAnchor] = useState<HTMLElement | null>(null);
  const [violationTypePaperWidth, setViolationTypePaperWidth] = useState(0);
  const [violationType, setViolationType] = useState<ViolationFilterTypeKey>('all');

  const defaultRange = useMemo(() => getDefaultViolationDateRange(), []);
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: defaultRange.start,
    end: defaultRange.end,
  });

  const [bc02Snapshot, setBc02Snapshot] = useState<Bc02ApiSnapshot>(() => ({ headers: [], data: [] }));
  const [reportLoading, setReportLoading] = useState(false);
  const lastFetchKeyRef = useRef<string>('');
  const reportFetchInFlightRef = useRef(0);

  const [sortBy, setSortBy] = useState<string>();
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirectionEnum.ASC);

  const hasBc02FromApi = bc02Snapshot.headers.length > 0;

  const headCells = useMemo(() => {
    if (hasBc02FromApi) return transformBc01HeadCells(bc02Snapshot.headers);
    return [];
  }, [hasBc02FromApi, bc02Snapshot.headers]);

  const transformedRows = useMemo(() => {
    if (!hasBc02FromApi) return [];
    return transformBc01Rows(bc02Snapshot.headers, bc02Snapshot.data);
  }, [bc02Snapshot.headers, bc02Snapshot.data, hasBc02FromApi]);

  const defaultEmptyMessage = !selectedPersonId ? t('filters.selectSubjectToViewReport') : undefined;

  const displayRows = useMemo(() => {
    let rows = transformedRows;
    if (rows.length === 0 && !hasBc02FromApi) {
      // Khi chưa chọn đối tượng, hiển thị empty state thay vì row giả.
      rows = defaultEmptyMessage ? [] : [{ id: 'placeholder' } as Bc01TableRow];
    }
    if (!sortBy) return rows;
    const mul = sortOrder === SortDirectionEnum.ASC ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = String(a[sortBy] ?? '');
      const vb = String(b[sortBy] ?? '');
      return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) * mul;
    });
  }, [transformedRows, sortBy, sortOrder, hasBc02FromApi, defaultEmptyMessage]);

  const violationTypeTriggerLabel = useMemo(() => {
    if (violationType === 'all') return t('violation.violationTypes.summary');
    return t(`violation.violationTypes.${violationType}`);
  }, [t, violationType]);

  const reportRangeStartMs = dateRange.start !== null ? dateRange.start.getTime() : null;
  const reportRangeEndMs = dateRange.end !== null ? dateRange.end.getTime() : null;

  const calendarLabels = useMemo(
    () => ({
      buttonLabel: tCommon('calendarPicker.buttonLabel'),
      timeLabel: tCommon('calendarPicker.timeLabel'),
      clickOrDragToSelect: tCommon('calendarPicker.clickOrDragToSelect'),
      dateInputHint: tCommon('calendarPicker.clickOrDragToSelect'),
      invalidDateFormat: tCommon('calendarPicker.invalidDateFormat'),
      startDatePlaceholder: tCommon('calendarPicker.startDate'),
      endDatePlaceholder: tCommon('calendarPicker.endDate'),
      previousMonth: tCommon('calendarPicker.previousMonth'),
      nextMonth: tCommon('calendarPicker.nextMonth'),
      todayNote: tCommon('calendarPicker.todayNote'),
      invalidDateRange: tCommon('calendarPicker.invalidDateRange'),
      endBeforeStart: tCommon('calendarPicker.endBeforeStart'),
      futureDateNotAllowed: tCommon('calendarPicker.futureDateNotAllowed'),
      weekdays: [
        tCommon('calendarPicker.weekdays.mon'),
        tCommon('calendarPicker.weekdays.tue'),
        tCommon('calendarPicker.weekdays.wed'),
        tCommon('calendarPicker.weekdays.thu'),
        tCommon('calendarPicker.weekdays.fri'),
        tCommon('calendarPicker.weekdays.sat'),
        tCommon('calendarPicker.weekdays.sun'),
      ] as [string, string, string, string, string, string, string],
    }),
    [tCommon]
  );

  const violationTypeOpen = Boolean(violationTypeAnchor);

  useEffect(() => {
    if (!username || reportRangeStartMs === null || reportRangeEndMs === null) {
      setBc02Snapshot({ headers: [], data: [] });
      lastFetchKeyRef.current = '';
      return;
    }

    if (!listReady || personsCount === 0) {
      return;
    }

    if (!selectedPersonId) {
      setBc02Snapshot({ headers: [], data: [] });
      lastFetchKeyRef.current = '';
      return;
    }

    const personsParam: string[] = [selectedPersonId];
    const nextKey = `${username}|${reportRangeStartMs}|${reportRangeEndMs}|${violationType}|${personsParam.join(',')}`;
    if (nextKey === lastFetchKeyRef.current) return;

    let stale = false;
    const startMs = reportRangeStartMs;
    const endMs = reportRangeEndMs;

    const timer = window.setTimeout(() => {
      (async () => {
        trackReportFetchStart(reportFetchInFlightRef);
        setReportLoading(true);
        try {
          const { from, to } = formatBc01FromTo(startMs, endMs);
          const params: IBc02Params = {
            username,
            from,
            to,
            persons: personsParam,
            type: bc02ViolationTypeParam(violationType),
          };
          const res = await reportApi.getBc02(params);
          if (stale) return;
          const { headers, data: rows } = parseBc01ResponsePayload(res.data);
          setBc02Snapshot({ headers, data: rows });
          lastFetchKeyRef.current = nextKey;
        } catch (e: unknown) {
          if (stale) return;
          if (axios.isCancel(e)) return;
          setBc02Snapshot({ headers: [], data: [] });
          handleApiError(e);
        } finally {
          trackReportFetchEnd(reportFetchInFlightRef, setReportLoading);
        }
      })();
    }, BC02_FETCH_DEBOUNCE_MS);

    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [
    username,
    reportRangeStartMs,
    reportRangeEndMs,
    selectedPersonId,
    listReady,
    personsCount,
    violationType,
  ]);

  const openViolationTypeMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const el = event.currentTarget;
    setViolationTypePaperWidth(el.offsetWidth);
    setViolationTypeAnchor(el);
  }, []);

  const closeViolationTypeMenu = useCallback(() => {
    setViolationTypeAnchor(null);
  }, []);

  const handleSelectViolationType = useCallback(
    (key: ViolationFilterTypeKey) => {
      setViolationType(key);
      closeViolationTypeMenu();
    },
    [closeViolationTypeMenu]
  );

  useLayoutEffect(() => {
    if (!violationTypeOpen || !violationTypeAnchor) return;
    const el = violationTypeAnchor;
    let rafId = 0;
    let lastW = -1;

    const apply = () => {
      const w = el.offsetWidth;
      if (lastW >= 0 && Math.abs(w - lastW) <= 1) return;
      lastW = w;
      setViolationTypePaperWidth(w);
    };

    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        apply();
      });
    };

    apply();
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
      };
    }
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [violationTypeOpen, violationTypeAnchor]);

  useEffect(() => {
    if (!violationTypeOpen) return;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') closeViolationTypeMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [violationTypeOpen, closeViolationTypeMenu]);

  const handleDateRangeChange = useCallback((start: Date | null, end: Date | null) => {
    setDateRange({ start, end });
  }, []);

  const handleStartDateChange = useCallback(
    (date: string | null) => {
      const start = date ? dayjs(date).toDate() : null;
      handleDateRangeChange(start, dateRange.end);
    },
    [dateRange.end, handleDateRangeChange]
  );

  const handleEndDateChange = useCallback(
    (date: string | null) => {
      const end = date ? dayjs(date).toDate() : null;
      handleDateRangeChange(dateRange.start, end);
    },
    [dateRange.start, handleDateRangeChange]
  );

  const handleClearFilters = useCallback(() => {
    const d = getDefaultViolationDateRange();
    setSelectedPersonId(null);
    setViolationType('all');
    setDateRange({ start: d.start, end: d.end });
    closeViolationTypeMenu();
  }, [closeViolationTypeMenu]);

  const handleExport = useCallback(() => {
    const { headers, data } = bc02Snapshot;
    if (data.length === 0) {
      notify('warning', t('exportNoData'));
      return;
    }
    const fileName = buildReportExcelFileName(t('violation.exportFileName'));
    const exportData = mapJourneyStatusColumnInRows(headers, data, i18n.language);
    queueMicrotask(() => {
      downloadBc01ReportExcel(headers, exportData, { fileName });
    });
  }, [bc02Snapshot, t, i18n.language]);

  const handleRequestSort = useCallback((sortField: string, order: SortDirection) => {
    setSortBy(sortField);
    setSortOrder(order);
  }, []);

  const renderCell = useCallback(
    (row: Bc01TableRow) => {
      const cell = REPORT_TABLE_CELL_CLASS;
      return (
        <>
          {headCells.map((hc) => {
            const raw = String(row[hc.id as string] ?? '');
            return (
              <TableCell key={hc.id} className={cell}>
                <ReportTableMultilineCell text={raw} />
              </TableCell>
            );
          })}
        </>
      );
    },
    [headCells]
  );

  return (
    <Box className={cx('report-screen', 'violation-report-page')}>
      <Box
        className='flex justify-between items-center h-20 px-6'
        sx={{
          background: 'var(--Brand-Primary-color_brand_primary, #123B6D)',
        }}
      >
        <Typography className='font-semibold text-xl' sx={{ color: '#fff' }}>
          {t('violation.title')}
        </Typography>
        <Button
          className={cx('export-button')}
          variant='contained'
          disableElevation
          endIcon={<ReportsExcelIcon className={cx('export-button-icon')} width={20} height={20} />}
          onClick={handleExport}
        >
          <span className={cx('export-button-label')}>{t('export')}</span>
        </Button>
      </Box>

      <Box className={cx('report-filter-row')}>
        <ReportSubjectFilter
          username={username}
          triggerId='violation-report-subject-trigger'
          selectedPersonId={selectedPersonId}
          onSelectedPersonIdChange={setSelectedPersonId}
          onListReadyChange={(ready, count) => {
            setListReady(ready);
            setPersonsCount(count);
          }}
        />

        <Box className={cx('report-filter-field')}>
          <span className={cx('report-filter-field-label')}>{t('violation.filters.violationType')}</span>
          <Button
            id='violation-type-trigger'
            variant='text'
            disableRipple
            className={cx('subject-trigger')}
            aria-haspopup='listbox'
            aria-expanded={violationTypeOpen ? 'true' : undefined}
            onClick={openViolationTypeMenu}
          >
            <div className={cx('subject-trigger-body')}>
              <span className={cx('subject-trigger-value')}>{violationTypeTriggerLabel}</span>
            </div>
            <SubjectArrowDownIcon
              className={cx('subject-trigger-arrow', violationTypeOpen && 'subject-trigger-arrow-open')}
              width={20}
              height={20}
              aria-hidden
            />
          </Button>
          <Popper
            open={violationTypeOpen}
            anchorEl={violationTypeAnchor}
            placement='bottom-start'
            transition
            modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            sx={{ zIndex: (theme) => theme.zIndex.modal }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                <div>
                  <ClickAwayListener
                    onClickAway={(event) => {
                      const anchor = violationTypeAnchor;
                      if (!anchor) return;
                      if (anchor.contains(event.target as Node)) return;
                      closeViolationTypeMenu();
                    }}
                    mouseEvent='onMouseDown'
                    touchEvent='onTouchStart'
                  >
                    <Paper
                      className={cx('subject-menu-paper')}
                      elevation={8}
                      sx={{
                        width: violationTypePaperWidth > 0 ? violationTypePaperWidth : undefined,
                        maxWidth: 'calc(100vw - 32px)',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box className={cx('subject-menu-list')} role='listbox' sx={{ pt: 1, pb: 1 }}>
                        {VIOLATION_MENU_KEYS.map((key) => {
                          const selected = violationType === key;
                          const label =
                            key === 'all'
                              ? t('violation.violationTypes.summary')
                              : t(`violation.violationTypes.${key}`);
                          return (
                            <Box
                              key={key}
                              role='option'
                              aria-selected={selected}
                              className={cx('subject-menu-row', selected && 'subject-menu-row-selected')}
                              sx={{
                                mx: 1,
                                px: 1,
                                py: 1,
                                borderRadius: '8px',
                                cursor: 'pointer',
                              }}
                              onClick={() => handleSelectViolationType(key)}
                            >
                              <Typography className='text-sm text-[#161A1F]'>{label}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  </ClickAwayListener>
                </div>
              </Grow>
            )}
          </Popper>
        </Box>

        <Box className={cx('report-filter-field')}>
          <span className={cx('report-filter-field-label')}>{t('filters.time')}</span>
          <div className={cx('report-filter-date-range')}>
            <div className={cx('report-filter-date-range-picker')}>
              <BaseDatePicker
                uiVariant='calendarPicker'
                portalId='datePicker-portalId'
                className='w-full'
                placeholder={calendarLabels.startDatePlaceholder}
                value={dateRange.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined}
                onChange={handleStartDateChange}
                dateFormat='YYYY-MM-DD'
                calendarProps={{ popperClassName: styles.reportDatePickerPopper }}
              />
            </div>
            <span className={cx('report-filter-date-range-separator')} aria-hidden>
              -
            </span>
            <div className={cx('report-filter-date-range-picker')}>
              <BaseDatePicker
                uiVariant='calendarPicker'
                portalId='datePicker-portalId'
                className='w-full'
                placeholder={calendarLabels.endDatePlaceholder}
                value={dateRange.end ? dayjs(dateRange.end).format('YYYY-MM-DD') : undefined}
                onChange={handleEndDateChange}
                dateFormat='YYYY-MM-DD'
                calendarProps={{ popperClassName: styles.reportDatePickerPopper }}
              />
            </div>
          </div>
        </Box>

        <Box className={cx('report-filter-clear')}>
          <span className={cx('report-filter-field-label')} aria-hidden>
            &nbsp;
          </span>
          <Button variant='text' className={cx('clear-filter-button')} onClick={handleClearFilters}>
            {t('filters.clearFilters')}
          </Button>
        </Box>
      </Box>

      <Box className={`px-6 ${cx('report-table-wrap')}`} sx={{ position: 'relative' }}>
        {reportLoading && (
          <Box className={cx('report-table-status-overlay')}>
            <Typography className='text-[#404854]'>{t('tableLoading')}</Typography>
          </Box>
        )}
        <BaseTableReport<Bc01TableRow>
          scrollable
          hidePagination
          data={displayRows}
          headCells={headCells}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onRequestSort={handleRequestSort}
          renderCell={(row) => renderCell(row)}
          emptyMessage={defaultEmptyMessage}
          suppressEmptyState={reportLoading}
        />
      </Box>
    </Box>
  );
};

export default ReportViolations;
